import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ContentBox from 'components/CncUI/ContentBox';
import Typography from 'components/CncUI/Typography';
import { Tabs, Tab } from 'components/CncUI/Tabs';
import PageDots from 'components/CncUI/PageDots';
import FileViewer from 'components/FileViewer';
import FileDownloader from 'components/FileDownloader';
import HistoryDocument from './HistoryDocument';

import useData from 'hooks/useData';
import useQuery from 'hooks/useQuery';
import base64ToArrayBuffer from 'utilities/base64ToArrayBuffer';

const W9 = 'W9';
const LOI = 'LOI';

const useStyles = makeStyles(({ palette }) => ({
  title: {
    color: palette.cncUI.default['-5'],
  },
  pagination: {
    padding: '0 24px 24px',
    textAlign: 'center',
  },
}));

const HistorySection = ({ list }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(list?.W9?.length ? W9 : LOI);
  const [viewer, setViewer] = useState('');
  const [downloader, setDownloader] = useState('');
  const [pagination, updatePagination] = useData({ W9: 0, LOI: 0 });
  const getLoanoutUrl = useQuery('getLoanoutUrl', {
    scopes: 'payroll_employee:edit',
    request: id => ({ id }),
    response: res => res.data.getLoanoutUrl || '',
  });
  const getLoanoutFile = useQuery('getLoanoutFile', {
    scopes: 'payroll_employee:edit',
    request: id => ({ id }),
    response: res => {
      const data = res.data.getLoanoutFile || '';
      if (data) {
        const arraybuffer = base64ToArrayBuffer(data);
        const fileBlob = new Blob([arraybuffer], { type: 'application/pdf' });
        return URL.createObjectURL(fileBlob);
      }
      return '';
    },
  });

  if (!list?.W9?.length && !list?.LOI.length) {
    return null;
  }

  const items = type =>
    (list?.[type] || []).slice(
      pagination[type] * 10,
      pagination[type] * 10 + 10,
    );

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} contained>
          {list.W9.length && <Tab value={W9}>W-9s</Tab>}
          {list.LOI.length && <Tab value={LOI}>Letters of Indemnification</Tab>}
        </Tabs>
      </Grid>
      <Grid item xs={12}>
        <ContentBox style={{ padding: 0 }}>
          {tab === W9 && (
            <>
              <ContentBox dense transparent style={{ padding: 24 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h5" noMargin className={classes.title}>
                      W-9 Documents History
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" size="short" noMargin>
                      A history of all your submitted W9 documents are listed
                      below.
                    </Typography>
                  </Grid>
                </Grid>
              </ContentBox>
              <Grid container spacing={0} style={{ paddingBottom: 24 }}>
                {items('W9').map((item, index) => (
                  <Grid item xs={12} key={item.id}>
                    <HistoryDocument
                      item={item}
                      current={index === 0 && pagination.W9 === 0}
                      view={setViewer}
                      download={setDownloader}
                    />
                  </Grid>
                ))}
              </Grid>
              <ContentBox dense transparent className={classes.pagination}>
                <PageDots
                  limit={10}
                  position={pagination.W9}
                  total={list?.W9?.length}
                  onChange={page => updatePagination({ W9: page })}
                />
              </ContentBox>
            </>
          )}
          {tab === LOI && (
            <>
              <ContentBox dense transparent style={{ padding: 24 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h5" noMargin className={classes.title}>
                      Letter of Indemnification History
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" size="short" noMargin>
                      A history of all your submitted agreements are listed
                      below.
                    </Typography>
                  </Grid>
                </Grid>
              </ContentBox>
              <Grid container spacing={0} style={{ paddingBottom: 24 }}>
                {items('LOI').map((item, index) => (
                  <Grid item xs={12} key={item.id}>
                    <HistoryDocument
                      item={item}
                      current={index === 0 && pagination.LOI === 0}
                      view={setViewer}
                      download={setDownloader}
                    />
                  </Grid>
                ))}
              </Grid>
              <ContentBox dense transparent className={classes.pagination}>
                <PageDots
                  limit={10}
                  position={pagination.LOI}
                  total={list?.LOI?.length}
                  onChange={page => updatePagination({ LOI: page })}
                />
              </ContentBox>
            </>
          )}
        </ContentBox>
      </Grid>
      {viewer && (
        <FileViewer
          fileFetcher={getLoanoutFile}
          config={viewer}
          onClose={() => setViewer('')}
        />
      )}
      {downloader && (
        <FileDownloader
          urlFetcher={getLoanoutUrl}
          config={downloader}
          onClose={() => setDownloader('')}
        />
      )}
    </Grid>
  );
};

export default HistorySection;
